import { useEffect, useState } from "react";
import api, { API, formatCNPJ, formatCPF } from "@/lib/api";
import { Stethoscope, Plus, Send, FileText, Mail, MessageCircle, CheckCircle2, X, Upload, Image as ImageIcon, Eye, Clock } from "lucide-react";
import { toast } from "sonner";
import { PARTNER_STATUS_LABELS } from "@/lib/services";
import CredenciadoForm from "@/components/CredenciadoForm";

const VIEW = { LIST: "list", FORM: "form", DETAIL: "detail", INVITE: "invite" };

export default function GestorCredenciados() {
  const [view, setView] = useState(VIEW.LIST);
  const [partners, setPartners] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const [p, i] = await Promise.all([
      api.get("/partners/all").then(r=>r.data).catch(()=>[]),
      api.get("/partners/invitations/list").then(r=>r.data).catch(()=>[]),
    ]);
    setPartners(p);
    setInvitations(i);
  };
  useEffect(()=>{ load(); }, []);

  if (view === VIEW.FORM) {
    return (
      <div className="space-y-4" data-testid="credenciamento-form-view">
        <button onClick={()=>setView(VIEW.LIST)} className="text-sm text-slate-500 hover:text-[#0E2A5C]"><X className="h-4 w-4 inline" /> Voltar</button>
        <h1 className="font-display text-3xl font-black text-[#0E2A5C]">Novo credenciamento</h1>
        <CredenciadoForm onSaved={(p)=>{ load(); setSelected(p); setView(VIEW.DETAIL); }} />
      </div>
    );
  }

  if (view === VIEW.INVITE) {
    return <InvitationView onBack={()=>{ setView(VIEW.LIST); load(); }} />;
  }

  if (view === VIEW.DETAIL && selected) {
    return <PartnerDetail partner={selected} onBack={()=>{ setSelected(null); setView(VIEW.LIST); load(); }} onChanged={async ()=>{ const fresh = await api.get(`/partners/${selected.id}`); setSelected(fresh.data); load(); }} />;
  }

  return (
    <div className="space-y-6" data-testid="credenciamento-list-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-black text-[#0E2A5C] flex items-center gap-2"><Stethoscope className="h-7 w-7" /> Credenciamento</h1>
          <p className="text-sm text-slate-500">{partners.length} credenciado(s) • {invitations.filter(i=>i.status==="pendente").length} convite(s) pendente(s)</p>
        </div>
        <div className="flex gap-2">
          <button onClick={()=>setView(VIEW.INVITE)} className="btn-outline text-sm" data-testid="btn-send-invitation"><Send className="h-4 w-4" /> Enviar convite</button>
          <button onClick={()=>setView(VIEW.FORM)} className="btn-primary text-sm" data-testid="btn-new-partner"><Plus className="h-4 w-4" /> Cadastrar manualmente</button>
        </div>
      </div>

      {invitations.length > 0 && (
        <div className="card-soft p-5">
          <h2 className="font-display text-lg font-bold text-[#0E2A5C] mb-3 flex items-center gap-2"><Send className="h-4 w-4" /> Convites enviados</h2>
          <div className="divide-y divide-slate-100">
            {invitations.map(inv => (
              <div key={inv.id} className="py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`invitation-${inv.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-[#0E2A5C] text-sm truncate">{inv.name}</div>
                  <div className="text-xs text-slate-500">{inv.email} • {inv.method.toUpperCase()} • Token: <span className="font-mono">{inv.token.slice(0,8)}...</span></div>
                </div>
                <span className="text-xs font-bold px-3 py-1 rounded-full" style={{background: inv.status==="preenchido"?"#D1FAE5":inv.status==="expirado"?"#FEE2E2":"#FEF3C7", color: inv.status==="preenchido"?"#065F46":inv.status==="expirado"?"#991B1B":"#92400E"}}>
                  {inv.status === "pendente" ? "Aguardando proponente" : inv.status === "preenchido" ? "Preenchido" : "Expirado"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card-soft overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="px-5 py-3 font-bold text-[#0E2A5C]">Credenciado</th>
              <th className="px-5 py-3 font-bold text-[#0E2A5C]">Documento</th>
              <th className="px-5 py-3 font-bold text-[#0E2A5C]">Cidade</th>
              <th className="px-5 py-3 font-bold text-[#0E2A5C]">Modalidade</th>
              <th className="px-5 py-3 font-bold text-[#0E2A5C]">Status</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {partners.map(p => {
              const status = PARTNER_STATUS_LABELS[p.status] || PARTNER_STATUS_LABELS.ativo;
              return (
                <tr key={p.id} className="hover:bg-slate-50 cursor-pointer" onClick={()=>{ setSelected(p); setView(VIEW.DETAIL); }} data-testid={`partner-row-${p.id}`}>
                  <td className="px-5 py-3 font-semibold text-[#0E2A5C]">{p.name}</td>
                  <td className="px-5 py-3 font-mono text-xs">{p.person_type==="PF" ? formatCPF(p.document) : formatCNPJ(p.document || "")}</td>
                  <td className="px-5 py-3">{p.address?.city || p.city || "—"}/{p.address?.state || p.state || ""}</td>
                  <td className="px-5 py-3 text-xs">{p.partnership_type==="tabela" ? "Tabela" : `Desconto ${p.discount_tier || p.discount_percent || "—"}%`}</td>
                  <td className="px-5 py-3"><span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{background: status.bg, color: status.color}} data-testid={`status-${p.id}`}>{status.label}</span></td>
                  <td className="px-5 py-3 text-right text-slate-400 hover:text-[#2A8FE0]"><Eye className="h-4 w-4" /></td>
                </tr>
              );
            })}
            {partners.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-slate-500">Nenhum credenciado.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InvitationView({ onBack }) {
  const [form, setForm] = useState({ name: "", person_type: "PJ", document: "", email: "", phone: "", method: "email" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/partners/invitations", { ...form, document: form.document.replace(/\D/g,"") });
      setResult(data);
      toast.success(form.method === "email" ? "Convite enviado por e-mail!" : "Link gerado!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="invitation-view">
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-[#0E2A5C]"><X className="h-4 w-4 inline" /> Voltar</button>
      <h1 className="font-display text-3xl font-black text-[#0E2A5C]">Enviar convite de credenciamento</h1>
      <p className="text-sm text-slate-500">O proponente recebe um link único para preencher os próprios dados. Após enviar, você valida e gera o contrato.</p>

      {!result && (
        <form onSubmit={submit} className="card-soft p-6 grid sm:grid-cols-2 gap-3 max-w-3xl">
          <div className="sm:col-span-2 grid grid-cols-2 gap-2">
            {["PJ","PF"].map(t => (
              <button key={t} type="button" onClick={()=>setForm({...form, person_type: t})} className={`py-3 rounded-xl border text-sm font-semibold transition ${form.person_type===t ? "border-[#2A8FE0] bg-blue-50 text-[#0E2A5C]" : "border-slate-200 text-slate-500"}`} data-testid={`invite-type-${t}`}>
                {t === "PF" ? "Pessoa Física" : "Pessoa Jurídica"}
              </button>
            ))}
          </div>
          <input required placeholder="Nome / Razão social" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="sm:col-span-2 px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="invite-name" />
          <input required placeholder={form.person_type==="PF"?"CPF":"CNPJ"} value={form.person_type==="PF"?formatCPF(form.document):formatCNPJ(form.document)} onChange={(e)=>setForm({...form, document: e.target.value.replace(/\D/g,"")})} className="px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="invite-document" />
          <input required placeholder="Telefone / celular" value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} className="px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="invite-phone" />
          <input required type="email" placeholder="E-mail" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} className="sm:col-span-2 px-3 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-[#2A8FE0]" data-testid="invite-email" />

          <div className="sm:col-span-2">
            <label className="block text-sm font-medium mb-2">Como enviar?</label>
            <div className="grid grid-cols-2 gap-2">
              {[["email", Mail, "E-mail (SendGrid)"], ["whatsapp", MessageCircle, "WhatsApp (wa.me)"]].map(([id, Icon, label]) => (
                <button key={id} type="button" onClick={()=>setForm({...form, method: id})} className={`p-3 rounded-xl border text-sm font-semibold transition flex items-center gap-2 justify-center ${form.method===id ? "border-[#2A8FE0] bg-blue-50 text-[#0E2A5C]" : "border-slate-200 text-slate-500"}`} data-testid={`invite-method-${id}`}>
                  <Icon className="h-4 w-4" /> {label}
                </button>
              ))}
            </div>
          </div>

          <button type="submit" disabled={busy} className="btn-primary sm:col-span-2 disabled:opacity-50" data-testid="invite-submit">
            {busy ? "Enviando..." : "Gerar e enviar convite"}
          </button>
        </form>
      )}

      {result && (
        <div className="card-soft p-6 space-y-3 max-w-2xl" data-testid="invite-result">
          <div className="flex items-center gap-2 text-emerald-700 font-bold"><CheckCircle2 className="h-5 w-5" /> Convite gerado</div>
          <div className="text-sm">Link único:</div>
          <div className="rounded-xl bg-slate-50 px-3 py-2.5 font-mono text-xs break-all border border-slate-100" data-testid="invite-link">{result.delivery.link}</div>
          {result.delivery.method === "whatsapp" && result.delivery.whatsapp_url && (
            <a href={result.delivery.whatsapp_url} target="_blank" rel="noreferrer" className="btn-primary inline-flex" data-testid="invite-wa-button"><MessageCircle className="h-4 w-4" /> Abrir WhatsApp</a>
          )}
          {result.delivery.method === "email" && (
            <div className={`text-sm font-semibold ${result.delivery.sent ? "text-emerald-700" : "text-amber-700"}`}>
              {result.delivery.sent ? "✅ E-mail enviado via SendGrid" : "⚠️ Falha no envio — copie o link manualmente"}
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <button onClick={()=>{ navigator.clipboard.writeText(result.delivery.link); toast.success("Link copiado!"); }} className="btn-outline text-sm" data-testid="copy-invite-link">Copiar link</button>
            <button onClick={()=>{ setResult(null); setForm({ name: "", person_type: "PJ", document: "", email: "", phone: "", method: "email" }); }} className="btn-outline text-sm">Novo convite</button>
            <button onClick={onBack} className="btn-primary text-sm">Concluir</button>
          </div>
        </div>
      )}
    </div>
  );
}

function PartnerDetail({ partner, onBack, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [signedFile, setSignedFile] = useState(null);
  const status = PARTNER_STATUS_LABELS[partner.status] || PARTNER_STATUS_LABELS.ativo;
  const token = localStorage.getItem("solimoes_token");

  const upload = async (kind, file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const url = kind === "logo" ? `/partners/${partner.id}/logo` : kind === "image" ? `/partners/${partner.id}/images` : `/partners/${partner.id}/signed-contract`;
      await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Upload concluído");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro no upload");
    } finally { setBusy(false); }
  };

  const generateContract = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/partners/${partner.id}/contract/generate`);
      toast.success(data.email_sent ? `Contrato gerado e enviado para ${data.email_to}` : "Contrato gerado (e-mail falhou)");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };
  const approve = async () => {
    setBusy(true);
    try {
      await api.put(`/partners/${partner.id}/approve`);
      toast.success("Credenciado liberado no catálogo!");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };

  const fileUrl = (path) => `${API}/files/${path}?auth=${token}`;

  return (
    <div className="space-y-5" data-testid="partner-detail-view">
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-[#0E2A5C]"><X className="h-4 w-4 inline" /> Voltar</button>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-black text-[#0E2A5C]">{partner.name}</h1>
          <div className="text-sm text-slate-500 mt-1">{partner.person_type} • {partner.person_type==="PF"?formatCPF(partner.document):formatCNPJ(partner.document || "")}</div>
        </div>
        <span className="text-sm font-bold px-4 py-1.5 rounded-full" style={{background: status.bg, color: status.color}} data-testid="detail-status">{status.label}</span>
      </div>

      {/* Status flow actions */}
      <div className="card-soft p-5 space-y-4" data-testid="status-actions">
        <h3 className="font-display text-lg font-bold text-[#0E2A5C]">Fluxo de credenciamento</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          <button disabled={busy || partner.status === "ativo"} onClick={generateContract} className="rounded-2xl border border-slate-100 p-4 text-left hover:border-[#2A8FE0] disabled:opacity-50" data-testid="action-generate-contract">
            <FileText className="h-5 w-5 text-[#2A8FE0]" />
            <div className="font-bold text-[#0E2A5C] mt-2">{partner.contract_pdf_path ? "Reenviar contrato" : "Gerar contrato"}</div>
            <div className="text-xs text-slate-500 mt-1">PDF + e-mail via SendGrid</div>
          </button>
          <label className={`rounded-2xl border border-slate-100 p-4 cursor-pointer hover:border-[#2A8FE0] block ${busy?"opacity-50":""}`} data-testid="action-upload-signed">
            <Upload className="h-5 w-5 text-[#2A8FE0]" />
            <div className="font-bold text-[#0E2A5C] mt-2">Upload contrato assinado</div>
            <div className="text-xs text-slate-500 mt-1">{partner.signed_pdf_path ? "✅ Já enviado — substituir" : "PDF assinado"}</div>
            <input type="file" accept="application/pdf" className="hidden" onChange={(e)=>upload("signed", e.target.files[0])} />
          </label>
          <button disabled={busy || partner.status === "ativo" || !partner.signed_pdf_path} onClick={approve} className="rounded-2xl border border-slate-100 p-4 text-left hover:border-emerald-500 disabled:opacity-50" data-testid="action-approve">
            <CheckCircle2 className="h-5 w-5 text-emerald-500" />
            <div className="font-bold text-[#0E2A5C] mt-2">Aprovar e ativar</div>
            <div className="text-xs text-slate-500 mt-1">Libera no catálogo público</div>
          </button>
        </div>
        {partner.contract_pdf_path && (
          <a href={fileUrl(partner.contract_pdf_path)} target="_blank" rel="noreferrer" className="text-sm text-[#2A8FE0] hover:underline inline-flex items-center gap-1" data-testid="link-contract-pdf"><FileText className="h-4 w-4" /> Baixar contrato gerado</a>
        )}
        {partner.signed_pdf_path && (
          <a href={fileUrl(partner.signed_pdf_path)} target="_blank" rel="noreferrer" className="text-sm text-emerald-700 hover:underline inline-flex items-center gap-1 ml-3" data-testid="link-signed-pdf"><FileText className="h-4 w-4" /> Baixar contrato assinado</a>
        )}
      </div>

      {/* Logo + Images */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="card-soft p-5" data-testid="logo-section">
          <h3 className="font-display text-lg font-bold text-[#0E2A5C] mb-3">Logo</h3>
          {partner.logo_path ? (
            <img src={fileUrl(partner.logo_path)} alt="Logo" className="w-full h-32 object-contain bg-slate-50 rounded-xl border border-slate-100" />
          ) : (
            <div className="w-full h-32 rounded-xl border border-dashed border-slate-200 flex items-center justify-center text-slate-400"><ImageIcon className="h-8 w-8" /></div>
          )}
          <label className="mt-3 btn-outline w-full text-sm cursor-pointer block text-center">
            <Upload className="h-4 w-4 inline" /> {partner.logo_path ? "Trocar logo" : "Enviar logo"}
            <input type="file" accept="image/*" className="hidden" onChange={(e)=>upload("logo", e.target.files[0])} data-testid="upload-logo" />
          </label>
        </div>

        <div className="card-soft p-5 lg:col-span-2" data-testid="images-section">
          <h3 className="font-display text-lg font-bold text-[#0E2A5C] mb-3">Imagens promocionais ({(partner.image_paths||[]).length}/3)</h3>
          <div className="grid grid-cols-3 gap-3">
            {[0,1,2].map(i => {
              const path = (partner.image_paths || [])[i];
              return path ? (
                <img key={i} src={fileUrl(path)} alt={`promo-${i}`} className="w-full h-32 object-cover rounded-xl border border-slate-100" data-testid={`promo-image-${i}`} />
              ) : (
                <label key={i} className="w-full h-32 rounded-xl border border-dashed border-slate-200 flex items-center justify-center text-slate-400 cursor-pointer hover:border-[#2A8FE0] hover:text-[#2A8FE0]" data-testid={`upload-image-${i}`}>
                  <input type="file" accept="image/*" className="hidden" onChange={(e)=>upload("image", e.target.files[0])} />
                  <ImageIcon className="h-8 w-8" />
                </label>
              );
            })}
          </div>
        </div>
      </div>

      {/* Summary card */}
      <div className="card-soft p-5">
        <h3 className="font-display text-lg font-bold text-[#0E2A5C] mb-3">Resumo</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
          <div><div className="text-xs uppercase font-bold text-slate-500">Modalidade</div><div>{partner.partnership_type==="tabela" ? `Tabela própria (${(partner.price_table||[]).length} itens)` : `Desconto ${partner.discount_tier || partner.discount_percent || "—"}%`}</div></div>
          <div><div className="text-xs uppercase font-bold text-slate-500">Vigência</div><div>{partner.duration_months || "—"} meses • início {partner.start_date || "—"}</div></div>
          <div><div className="text-xs uppercase font-bold text-slate-500">Cidade</div><div>{partner.address?.city || partner.city || "—"}/{partner.address?.state || partner.state || ""}</div></div>
          <div><div className="text-xs uppercase font-bold text-slate-500">E-mail</div><div>{partner.contacts?.email || "—"}</div></div>
          <div><div className="text-xs uppercase font-bold text-slate-500">WhatsApp</div><div>{partner.contacts?.whatsapp || "—"}</div></div>
          <div><div className="text-xs uppercase font-bold text-slate-500">Site / Insta</div><div>{partner.contacts?.site || partner.contacts?.instagram || "—"}</div></div>
        </div>
        <div className="mt-3 text-xs text-slate-500">Serviços: {(partner.services || []).join(" • ")}</div>
      </div>
    </div>
  );
}
